'use client';

import banner from '@/assets/images/military-discount/banner.jpg';
import { Accordion, Section } from '@/components/Accordion';
import CommonBreadcrumbs from '@/components/CommonBreadcrumbs';
import Field from '@/components/Field';
import { fullPageLoadingIndicator } from '@/components/LoadingIndicator';
import TextInput from '@/components/TextInput';
import Upload from '@/components/Upload';
import useMilitaryDiscountPage from '@/hooks/MilitaryDiscountPage/useMilitaryDiscountPage';
import { isRequired, validateEmail } from '@/util/formValidators';
import clsx from 'clsx';
import { Form } from 'informed';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { Fragment, useEffect, useRef, useState } from 'react';
import NoSsr from '../no-ssr';
import classes from './militaryDiscountPage.module.css';

const StudentDiscountAndMilitaryDiscountDialog = dynamic(
  () => import('@/components/StudentDiscountAndMilitaryDiscountDialog'),
  { ssr: false },
);

const paths = [
  {
    text: 'Home',
    path: '/',
  },
  {
    text: 'military discount',
    path: '/military/discount',
    current: true,
  },
];

const MilitaryDiscountPage = () => {
  const uploadRef = useRef(null);

  const talonProps = useMilitaryDiscountPage({
    uploadRef,
  });

  const { isLoading, errors, handleSubmit, formApiRef, response } = talonProps;

  const maybeLoadingIndicator = isLoading ? fullPageLoadingIndicator : null;

  const maybeSuccessInfo =
    response && response.code === '200' ? (
      <span className={classes.successInfo}>
        We have received your application and will get back to you via email within 48 hours!
      </span>
    ) : null;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (response && response.code === '200') {
      setIsOpen(true);
    }
  }, [response]);

  return (
    <Fragment>
      {maybeLoadingIndicator}
      <StudentDiscountAndMilitaryDiscountDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <div className={classes.root}>
        <div className='hidden h-auto w-full lg_block'>
          <Image
            src={banner}
            height={500}
            className={classes.bannerImage}
            alt=''
          />
        </div>
        <div className={classes.contentContainer}>
          <CommonBreadcrumbs paths={paths} />
          <div className={classes.content}>
            <h1 className={classes.title}>15% Off Military Discount</h1>
            <p className={classes.subTitle}>
              15% off military discount for active and reservist military, veterans, retirees. Thank you for your work!
            </p>

            <Form
              className={classes.form}
              onSubmit={handleSubmit}
              formApiRef={formApiRef}
            >
              <div className={classes.twoFieldContainer}>
                <Field
                  id='first-name'
                  label={<span className='font-poppins text-title'>First Name</span>}
                  required
                >
                  <TextInput
                    autoComplete='firstname'
                    field='firstname'
                    id='firstName'
                    validate={isRequired}
                  />
                </Field>
                <Field
                  id='last-name'
                  label={<span className='font-poppins text-title'>Last Name</span>}
                  required
                >
                  <TextInput
                    autoComplete='lastname'
                    field='lastname'
                    id='lastName'
                    validate={isRequired}
                  />
                </Field>
              </div>
              <div className={classes.fieldContainer}>
                <Field
                  id='email'
                  label={<span className='font-poppins text-title'>Email</span>}
                  required
                >
                  <TextInput
                    autoComplete='email'
                    field='email'
                    id='Email'
                    validate={value => {
                      return validateEmail(typeof value === 'string' ? value.trim() : value);
                    }}
                  />
                </Field>
              </div>
              <div className={classes.fieldContainer}>
                <Field
                  id='photo'
                  label='Photo of your military ID:'
                  classes={{
                    label: classes.uploadLabel,
                  }}
                >
                  <NoSsr fallback={<div className='h-[100px] w-[100px]'></div>}>
                    <Upload
                      ref={uploadRef}
                      showUploadList={{
                        showDownloadIcon: false,
                      }}
                      validate={isRequired}
                    />
                  </NoSsr>
                  <div className={classes.picadd}>
                    <span>1 image, jpg/png only.</span>
                  </div>
                </Field>
              </div>
              <div className={classes.buttonContainer}>
                <button
                  type='submit'
                  className={clsx(
                    ' h-10 w-full max-w-[260px] lg_h-[54px]  ',
                    'bg-primary text-white lg_hover_opacity-90',
                    'font-poppins font-semibold uppercase',
                    'rounded-[10px]',
                  )}
                >
                  Verify Now
                </button>
              </div>
              {maybeSuccessInfo}
            </Form>

            <Accordion
              canOpenMultiple
              classes={{ root: 'mt-20' }}
            >
              <Section
                isOpen
                className={classes.section}
                id={'section-faq'}
                title={<h2 className='text-primary'>Frequently Asked Questions</h2>}
                classes={{
                  title: clsx(' text-xl leading-[1.2] font-poppins text-title uppercase h-auto'),
                  title_wrapper: clsx(
                    'flex items-center justify-between gap-x-1 py-[11px] border-b-1 border-solid border-[#f0f0f0]',
                  ),
                }}
              >
                <p className={classes.sectionTitle}>1. Do you offer a military discount?</p>
                <p className='font-normal'>
                  Yes, we proudly offer a 12% discount to U.S. military members as a thank-you for your service.
                </p>
                <p className={classes.sectionTitle}>2. Who is eligible?</p>
                <p className='font-normal'>
                  Active duty, veterans, retirees, reservists, and National Guard members are eligible. Immediate family
                  members may also qualify with verification.
                </p>
                <p className={classes.sectionTitle}>3. How do I claim my military discount?</p>
                <p className='font-normal'>
                  You will be asked to upload your certification or teacher&apos;s ID card. Once approved, you will get
                  the discount from our support team.
                </p>

                <p className={classes.sectionTitle}>4. Can I use the military discount with other promotions?</p>

                <p className='font-normal'>
                  No, the military discount cannot be stacked with other discounts or promotional codes. You will always
                  receive the best available price at checkout.
                </p>

                <p className={classes.sectionTitle}>5. Do I need to re-verify every time?</p>

                <p className='font-normal'>
                  No, once verified, your military discount remains active for future 3 purchases in a year, though
                  re-verification may occasionally be required.
                </p>
                <p className={classes.sectionTitle}>6. Is the military discount available in offline stores?</p>
                <p className='font-normal'>
                  Currently, the discount is only available for online purchases made on our official website.
                </p>
              </Section>
            </Accordion>

            <Accordion canOpenMultiple>
              <Section
                isOpen
                className={clsx(classes.section)}
                id={'section-terms'}
                title={<h2 className='text-primary'>Military Discounts Terms and Conditions</h2>}
                classes={{
                  title: clsx(' text-xl leading-[1.2] font-poppins text-title uppercase h-auto'),
                  title_wrapper: clsx(
                    'flex items-center justify-between gap-x-1 py-[11px] border-b-1 border-solid border-[#f0f0f0]',
                  ),
                }}
              >
                <ul className='mt-10 list-inside list-disc font-normal leading-[38px]'>
                  <li>
                    The discount provides&nbsp;<span className=' text-title'>12% off your order</span>, excluding
                    shipping fees.
                  </li>
                  <li>
                    Only&nbsp;<span className=' text-title'>one discount code</span>&nbsp;can be used per order.
                  </li>
                  <li>
                    The discount is valid for&nbsp;<span className=' text-title'>full-price items only</span>.
                  </li>
                  <li>
                    Each coupon code is valid for&nbsp;<span className=' text-title'>1 month</span>. We can issue
                    only&nbsp;
                    <span className=' text-title'>one coupon code per verified ID within a 6-month period</span>.
                  </li>
                  <li>
                    The coupon must be used under the&nbsp;
                    <span className=' text-title'>same name and email address</span>&nbsp;used for verification. Orders
                    placed under different names may be cancelled without notice, and no additional codes will be
                    provided.
                  </li>
                  <li>
                    Previous orders are&nbsp;<span className=' text-title'>not eligible</span>&nbsp;for reimbursement.
                  </li>
                  <li>By applying, you confirm that you have read and agreed to these Terms & Conditions.</li>
                  <li>
                    All information submitted will be used&nbsp;
                    <span className=' text-title'>for verification purposes only</span>&nbsp;and will&nbsp;
                    <span className=' text-title'>not be collected or stored</span>&nbsp;by kingsyard.com.
                  </li>
                  <li>
                    If you encounter any issues receiving your code or have questions, please contact us at&nbsp;
                    <a
                      className=' text-title'
                      href='mailto:cs@e.kingsyard.com'
                    >
                      cs@e.kingsyard.com
                    </a>
                    .
                  </li>
                </ul>
              </Section>
            </Accordion>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default MilitaryDiscountPage;
